<template>
  <section
    id="acces"
    class="acces"
  >
    <div class="container">
      <SectionHead
        :icon="data.acces.icon"
        :title="data.acces.title"
      />

      <div class="acces__cols">
        <div class="acces__map-card">
          <img
            class="acces__map"
            :src="data.acces.map"
            alt="Carte d'accès au Centre Sud"
          >
          <div class="acces__address">
            <div>
              <p class="acces__address-title">{{ data.acces.addressTitle }}</p>
              <p class="acces__address-lines">{{ data.acces.address }}</p>
            </div>
            <a
              class="btn-teal"
              :href="data.acces.mapsUrl"
              target="_blank"
              rel="noopener"
            >{{ data.acces.mapsLabel }}</a>
          </div>
        </div>

        <div class="acces__info">
          <div class="acces__tabs">
            <button
              v-for="tab in data.acces.tabs"
              :key="tab.id"
              class="acces__tab"
              :class="{ 'is-active': tab.id === activeTab }"
              @click="activeTab = tab.id"
            >
              <span class="acces__tab-icons">
                <img
                  v-for="ico in tab.icons"
                  :key="ico"
                  :src="ico"
                  alt=""
                >
              </span>
              <span class="acces__tab-label">{{ tab.label }}</span>
            </button>
          </div>

          <!-- ligne : picto encadré + libellé coloré (tone : bleu/vert), **gras** -->
          <div class="acces__panel">
            <h3 class="acces__panel-title">{{ currentTab.heading }}</h3>
            <div
              v-for="(line, i) in currentTab.lines"
              :key="i"
              class="acces__line"
              :class="`is-${line.tone || 'bleu'}`"
            >
              <span
                v-if="line.icon"
                class="acces__line-icon"
                :style="{ '--icon': `url(${JSON.stringify(line.icon)})` }"
                aria-hidden="true"
              />
              <div class="acces__line-body">
                <p
                  v-if="line.tag || line.title"
                  class="acces__line-title"
                >
                  <span
                    v-if="line.tag"
                    class="acces__line-tag"
                  >{{ line.tag }}</span>
                  <span v-html="rich(line.title)" />
                </p>
                <p
                  v-for="(detail, j) in line.details"
                  :key="j"
                  class="acces__line-detail"
                  v-html="rich(detail)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import SectionHead from '@/components/SectionHead.vue'
import { rich } from '@/utils/rich'

export default {
  name: 'AccesSection',
  components: { SectionHead },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeTab: this.data.acces.tabs[0]?.id || 'transports'
    }
  },
  methods: {
    rich
  },
  computed: {
    currentTab() {
      return this.data.acces.tabs.find((t) => t.id === this.activeTab) || this.data.acces.tabs[0]
    }
  }
}
</script>

<style lang="scss" scoped>
// fond uni : contrairement aux boutiques, la maquette ne pose pas de filigrane ici
.acces {
  background: $bg-light;
  padding: 64px 0 72px;
}

.acces__cols {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 28px;
  margin-top: 40px;
  align-items: start;
}

.acces__map-card {
  background: $white;
  border-radius: 14px;
  padding: 22px;
  box-shadow: 0 3px 14px rgba($navy, 0.07);
}

.acces__map {
  width: 100%;
  height: auto;
  border-radius: 10px;
}

// adresse + bouton Maps sur la même ligne (bouton à droite, cf. maquette)
.acces__address {
  margin-top: 20px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;

  &-title {
    font-size: 16px;
    font-weight: 700;
    color: $ink;
  }

  &-lines {
    margin-top: 6px;
    font-size: 13.5px;
    line-height: 1.55;
    white-space: pre-line;
  }
}

// barre d'onglets : panneau bleu clair arrondi, picto au-dessus du libellé.
// Maquette : pictos et libellés noirs, onglet actif souligné d'un trait dégradé.
.acces__tabs {
  display: flex;
  background: #DCE8F3;
  border-radius: 14px;
  padding: 6px;
}

.acces__tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 14px 8px 12px;
  font-family: inherit;
  color: $ink;

  // pictos dimensionnés par la hauteur : le vélo, très large, n'est plus écrasé
  &-icons {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    height: 34px;

    img {
      height: 34px;
      width: auto;
      transition: transform 0.2s ease;
    }
  }

  &-label {
    font-size: 12.5px;
    font-weight: 500;
    text-align: center;
    line-height: 1.25;
    padding-bottom: 9px;
    position: relative;

    // trait dégradé vert -> bleu, sous le libellé de l'onglet actif
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 3px;
      border-radius: 2px;
      background: linear-gradient(90deg, $green, $blue);
      transform: scaleX(0);
      transition: transform 0.25s ease;
    }
  }

  &:hover &-icons img {
    transform: translateY(-2px);
  }

  &.is-active &-label::after {
    transform: scaleX(1);
  }
}

.acces__panel {
  margin-top: 18px;
  background: $white;
  border-radius: 14px;
  padding: 26px 28px 12px;
  box-shadow: 0 3px 14px rgba($navy, 0.07);

  &-title {
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: $teal;
    padding-bottom: 16px;
  }
}

// lignes séparées par un filet clair qui déborde jusqu'aux bords du panneau
.acces__line {
  --tone: #{$teal};
  display: flex;
  gap: 14px;
  margin: 0 -14px;
  padding: 14px 14px;
  border-top: 1px solid #E3ECF1;

  &.is-vert {
    --tone: #{$green};
  }

  // picto encadré (carré arrondi), trait et picto à la couleur de la ligne
  &-icon {
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
    border: 1.5px solid var(--tone);
    border-radius: 6px;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      inset: 4px;
      background: var(--tone);
      -webkit-mask: var(--icon) no-repeat center / contain;
      mask: var(--icon) no-repeat center / contain;
    }
  }

  &-body {
    padding-top: 5px;
  }

  &-title {
    font-size: 14.5px;
    line-height: 1.4;
    color: $ink;
  }

  &-tag {
    margin-right: 0.3em;
    font-weight: 700;
    color: var(--tone);
  }

  &-detail {
    font-size: 14px;
    line-height: 1.5;
    color: $ink;

    // 1re ligne sans titre (vélo, voiture) : alignée sur le picto
    & + &,
    .acces__line-title + & {
      margin-top: 12px;
    }
  }

  :deep(strong) {
    font-weight: 700;
  }
}

@include tablet-down {
  .acces__cols {
    grid-template-columns: 1fr;
  }
}

@include mobile {
  .acces {
    padding: 48px 0 56px;
  }

  .acces__panel {
    padding: 20px;
  }

  .acces__tab {
    padding: 12px 4px 8px;

    &-label {
      font-size: 11px;
    }

    &-icons {
      height: 28px;
      gap: 5px;

      img {
        height: 28px;
      }
    }
  }
}
</style>
